import { useCallback, useEffect, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { DropResult } from 'react-beautiful-dnd'
import { normalizeCategoryName, parseTaskCategories } from 'components/TasksV2/model/categoryNames'
import { v4 as uuid } from 'uuid'
import { Content, Subtask, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { migrateSubtaskDefaults } from 'components/TasksV2/TaskCreation/model/subtaskDefaults'
import keys from 'constants/keys'
import { IContentFormData, INewCategory } from '../types'
import { IDraftData, loadDraft, saveDraft } from '../draftStore'
import { useDraftPersistence } from './useDraftPersistence'
import { categoryDraftToTaskContent, getEmptyCategories, taskContentToCategoryDraft } from '../model/categoryDraft'

/** Category membership is page-owned; RHF v5 owns the same registered editor fields as flat creation. */
export function useCategoryContent(onNext: (data: IContentFormData) => void, initialData?: IContentFormData) {
  const { setSubtaskActions, setCurrentFormSubmit, isMediaUploading, isSubtaskAiGenerating, getFormData, isEdit } =
    useFormStepContext()

  const [initial] = useState(() => initialData || loadDraft() || { title: '', categories: [] })
  const [categories, setCategories] = useState<Pick<INewCategory, 'id' | 'name'>[]>(initial.categories)
  const [activeCategory, setActiveCategory] = useState<string | null>(initial.categories[0]?.id || null)
  const [pendingCategory, setPendingCategory] = useState<string | null>(null)
  const [choosingCategory, setChoosingCategory] = useState(false)
  const [showRestoredDraft, setShowRestoredDraft] = useState(!isEdit && !initialData && 'savedAt' in initial)

  const methods = useForm({ defaultValues: migrateSubtaskDefaults(categoryDraftToTaskContent(initial, isEdit)) })

  const { control, getValues, handleSubmit, setValue, watch } = methods

  const { fields, append: appendField, insert, remove, move } = useFieldArray({ control, name: 'sub_tasks' })

  const busy = isMediaUploading || isSubtaskAiGenerating
  const values = watch({ nest: true })
  const active = categories.find((category) => category.id === activeCategory)

  const sourceRef = useRef({ categories, fields })

  sourceRef.current = { categories, fields }

  const readContent = useCallback(() => {
    const current = getValues({ nest: true }) as { title?: string; sub_tasks?: (Subtask | Content)[] }

    return {
      ...current,
      sub_tasks: sourceRef.current.fields.map((field, index) => ({ ...field, ...current.sub_tasks?.[index] })) as (
        | Subtask
        | Content
      )[],
    }
  }, [getValues])

  const readDraft = useCallback(
    () =>
      taskContentToCategoryDraft(
        readContent(),
        sourceRef.current.categories,
        sourceRef.current.fields.map((field) => field.id || '')
      ),
    [readContent]
  )

  const draft = taskContentToCategoryDraft(
    values,
    categories,
    fields.map((field) => field.id || '')
  )
  const emptyCategoryNames = getEmptyCategories(draft).map((category) => category.name)

  const readPersistedDraft = useCallback(
    () => ({
      ...readDraft(),
      settingsData: getFormData?.() || (initial as Partial<IDraftData>).settingsData,
      pageStep: 0 as const,
    }),
    [getFormData, initial, readDraft]
  )

  const append = useCallback(
    (subtask: Subtask | Content) => {
      if (busy || isEdit || !active) return

      const source = readContent().sub_tasks

      if (subtask.type === keys.SUBTASK_TYPES.DESCRIPTION) {
        const descriptions = source.filter((item) => 'index_ref' in item).length
        const value = { ...subtask, index_ref: descriptions }
        if (descriptions === source.length) appendField(value)
        else insert(descriptions, value)
        return
      }

      const groupIndex = categories.findIndex((category) => category.id === active.id)
      const laterNames = categories.slice(groupIndex + 1).map((category) => category.name)
      const nextGroupIndex = source.findIndex((item) => 'category' in item && laterNames.includes(item.category || ''))
      const value = { ...subtask, category: active.name }

      // RHF v5 insert() reads registered array leaves and crashes before the first leaf exists.
      // Appending at the end also avoids unregistering sibling upload/editor fields unnecessarily.
      if (nextGroupIndex < 0) appendField(value)
      else insert(nextGroupIndex, value)
    },
    [active, appendField, busy, isEdit, categories, insert, readContent]
  )

  const addCategories = (names: string[]) => {
    if (busy || isEdit) return
    const existing = new Set(categories.map((category) => normalizeCategoryName(category.name)))
    const added = parseTaskCategories(names.map((name) => ({ name })))
      .filter((category) => !existing.has(normalizeCategoryName(category.name)))
      .map((category) => ({ id: uuid(), name: category.name }))
    if (!added.length) return
    setCategories((previous) => [...previous, ...added])
    setActiveCategory(added[added.length - 1].id)
    setChoosingCategory(false)
  }

  const addCategory = (name: string) => addCategories([name])

  const reorderSubtasks = ({ draggableId, source, destination }: DropResult) => {
    if (busy || isEdit || !destination || source.droppableId !== destination.droppableId) return
    const from = fields.findIndex((field) => field.id === draggableId)
    const content = readContent().sub_tasks
    if (from < 0) return
    const category = (content[from] as Subtask).category
    const indices = content.flatMap((item, index) => {
      const itemCategory = (item as Subtask).category
      const inSameList = source.droppableId === 'creation-subtasks' ? !itemCategory : itemCategory === category
      return inSameList ? [index] : []
    })
    const to = indices[destination.index]
    if (to !== undefined && from !== to && (content[to] as Subtask).category === category) move(from, to)
  }

  const removeCategory = (id: string) => {
    if (busy || isEdit) return

    const category = categories.find((item) => item.id === id)

    if (!category) return

    const indices = readContent().sub_tasks.flatMap((subtask, index) =>
      'category' in subtask && subtask.category === category.name ? [index] : []
    )

    if (indices.length) remove(indices)

    const remaining = categories.filter((item) => item.id !== id)

    setCategories(remaining)
    if (activeCategory === id) setActiveCategory(remaining[0]?.id || null)
    setPendingCategory(null)
  }

  const submit = handleSubmit(() => {
    const current = readDraft()

    if (!current.categories.some((category) => category.subtasks.length) || getEmptyCategories(current).length) return
    saveDraft(readPersistedDraft())
    onNext(current)
  })

  const submitRef = useRef(submit)

  submitRef.current = submit

  useDraftPersistence(readPersistedDraft)

  useEffect(() => {
    setCurrentFormSubmit(() => () => submitRef.current())
  }, [setCurrentFormSubmit])

  useEffect(() => {
    setSubtaskActions({
      append,
      remove: (index) => {
        if (!busy && !isEdit) remove(index)
      },
      move: (from, to) => {
        if (busy || isEdit) return

        const source = readContent().sub_tasks

        // The category owns ordering; moving within it never silently changes category membership.
        if ((source[from] as Subtask)?.category === (source[to] as Subtask)?.category) move(from, to)
      },
      snapshotSubtasks: () => readContent() as { title?: string; sub_tasks: Subtask[] },
      startAiSetup: (index, source) => {
        if (busy || isEdit) return

        const subtask = source.sub_tasks?.[index]

        if (!subtask) return
        if (index >= fields.length) append({ ...subtask, is_ai_enhanced: true, ai_prompt: '' })
        else setValue(`sub_tasks[${index}].is_ai_enhanced`, true)
      },
    })
  }, [append, busy, isEdit, fields.length, move, readContent, remove, setSubtaskActions, setValue])

  return {
    methods,
    fields,
    busy,
    values,
    categories,
    draft,
    active,
    activeCategory,
    setActiveCategory,
    choosingCategory,
    setChoosingCategory,
    emptyCategoryNames,
    showRestoredDraft,
    setShowRestoredDraft,
    addCategory,
    addCategories,
    reorderSubtasks,
    pendingCategory,
    setPendingCategory,
    removeCategory,
  }
}
